import React from 'react';
import { ArcBox, ArcCellInfo, ArcStack, ArcText } from '@src/core/ui';
import { arcDateWithTime, arcFormatCoordinateDisplay, arcSpatium, arcVelocitas } from '@src/core/extensions';
import { EMPTY_STRING } from '@src/core/config';
import { OrderDetailsStatusColors } from '@src/core/constants/order-status-colors';
import {
  OrderEntity,
  OrderMissionEntity,
  OrderStatusEnum,
  OrderStatusNames,
  OrderWayPointEntity,
} from '@src/features/order/entities';
import { formatAltitudeMode, formatDeliveryMode } from '@src/features/order/utils';

type MissionDetailsVariant = 'popup' | 'detailed';

const VARIANT_STYLES: Record<MissionDetailsVariant, {
  rootSpacing: number;
  labelProps?: Record<string, unknown>;
  textProps: Record<string, unknown>;
  color?: string;
}> = {
  popup: {
    rootSpacing: 2,
    textProps: { fontWeight: 400 },
  },
  detailed: {
    rootSpacing: 2.5,
    labelProps: { fontSize: '14px' },
    textProps: { fontWeight: 400, fontSize: '16px' },
    color: 'white',
  },
};

type MissionDetailsGridProps = {
  order?: OrderMissionEntity | OrderEntity;
  variant?: MissionDetailsVariant;
};

const isHomeWaypoint = (w: OrderWayPointEntity): boolean => w.type === 'HOME';

const distance = (value?: string | number): string =>
  value != null && Number.isFinite(Number(value)) ? arcSpatium(Number(value), { fromSi: true }) : EMPTY_STRING;
const speed = (value?: string | number): string =>
  value != null && Number.isFinite(Number(value)) ? arcVelocitas(Number(value), { fromSi: true }) : EMPTY_STRING;

const getMissionInfo = (order?: OrderMissionEntity | OrderEntity) => {
  const orderData = (order ?? {}) as Record<string, any>;
  const nested =
    Array.isArray(orderData.orders) && orderData.orders.length > 0 ? orderData.orders[0] : orderData;

  const waypoints = (nested?.waypoints ?? orderData?.waypoints ?? []) as OrderWayPointEntity[];
  const routeWaypoints = waypoints.some(isHomeWaypoint)
    ? waypoints.filter((w) => !isHomeWaypoint(w))
    : waypoints.slice(1);
  const takeOffWp = routeWaypoints[0];
  const orderWp = routeWaypoints.find((w) => Boolean(w.orderId)) ?? routeWaypoints[routeWaypoints.length - 1];

  return {
    id: nested?.id ?? 'N/A',
    createDate: nested?.createDate,
    status: nested?.status,
    drone: orderData?.drone?.name ?? nested?.drone?.name,
    takeoff: nested?.takeoffLocation,
    destination: nested?.deliveryLocation,
    deliveryMode: nested?.deliveryMode,
    altitudeMode: nested?.altitudeMode ?? orderData?.altitudeMode,
    hub: nested?.hub?.name,
    orderSpeed: nested?.speed,
    packageContent: nested?.packageContent,
    clientName: nested?.clientName,
    takeOffWp,
    orderWp,
  };
};

const MissionDetailsGrid: React.FC<MissionDetailsGridProps> = ({ order, variant = 'popup' }) => {
  const info = getMissionInfo(order);
  const statusKey = info.status?.toLowerCase() as OrderStatusEnum;
  const style = VARIANT_STYLES[variant];
  const { labelProps, textProps, color } = style;

  return (
    <ArcStack my={1} spacing={style.rootSpacing} sx={color ? { color } : undefined}>
      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Order' width='50%' labelProps={labelProps} textProps={textProps}>#{info.id}</ArcCellInfo>
        <ArcCellInfo label='Date' width='50%' labelProps={labelProps} textProps={textProps}>
          {info.createDate ? arcDateWithTime(String(info.createDate)) : EMPTY_STRING}
        </ArcCellInfo>
      </ArcStack>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Status' width='50%' labelProps={labelProps} textProps={{ ...textProps, component: 'div' }}>
          <ArcStack direction='row' alignItems='center' spacing={1}>
            <ArcBox width={8} height={8} borderRadius='50%' bgcolor={OrderDetailsStatusColors[statusKey]} />
            <ArcText color={color} fontSize={14} fontWeight={400}>{OrderStatusNames[statusKey] ?? EMPTY_STRING}</ArcText>
          </ArcStack>
        </ArcCellInfo>
        <ArcCellInfo label='Drone' width='50%' labelProps={labelProps} textProps={textProps}>
          {info.drone || EMPTY_STRING}
        </ArcCellInfo>
      </ArcStack>

      <ArcCellInfo label='Takeoff Location' width='100%' labelProps={labelProps} textProps={textProps}>
        {info.takeoff?.address || EMPTY_STRING}
      </ArcCellInfo>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Takeoff Latitude' width='50%' labelProps={labelProps} textProps={textProps}>
          {arcFormatCoordinateDisplay(info.takeoff?.latitude, EMPTY_STRING)}
        </ArcCellInfo>
        <ArcCellInfo label='Takeoff Longitude' width='50%' labelProps={labelProps} textProps={textProps}>
          {arcFormatCoordinateDisplay(info.takeoff?.longitude, EMPTY_STRING)}
        </ArcCellInfo>
      </ArcStack>

      <ArcCellInfo label='Destination Location' width='100%' labelProps={labelProps} textProps={textProps}>
        {info.destination?.address || EMPTY_STRING}
      </ArcCellInfo>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Destination Latitude' width='50%' labelProps={labelProps} textProps={textProps}>
          {arcFormatCoordinateDisplay(info.destination?.latitude, EMPTY_STRING)}
        </ArcCellInfo>
        <ArcCellInfo label='Destination Longitude' width='50%' labelProps={labelProps} textProps={textProps}>
          {arcFormatCoordinateDisplay(info.destination?.longitude, EMPTY_STRING)}
        </ArcCellInfo>
      </ArcStack>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Takeoff Altitude' width='50%' labelProps={labelProps} textProps={textProps}>
          {distance(info.takeOffWp?.altitude ?? info.takeoff?.altitude)}
        </ArcCellInfo>
        <ArcCellInfo label='Destination Altitude' width='50%' labelProps={labelProps} textProps={textProps}>
          {distance(info.orderWp?.altitude ?? info.destination?.altitude)}
        </ArcCellInfo>
      </ArcStack>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Delivery Altitude' width='50%' labelProps={labelProps} textProps={textProps}>
          {distance(info.orderWp?.deliveryAltitude)}
        </ArcCellInfo>
        <ArcCellInfo label='Cruise Speed' width='50%' labelProps={labelProps} textProps={textProps}>
          {speed(info.orderWp?.speed ?? info.orderSpeed)}
        </ArcCellInfo>
      </ArcStack>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Altitude Mode' width='50%' labelProps={labelProps} textProps={textProps}>
          {formatAltitudeMode(info.altitudeMode) || EMPTY_STRING}
        </ArcCellInfo>
        <ArcCellInfo label='Delivery Mode' width='50%' labelProps={labelProps} textProps={textProps}>
          {formatDeliveryMode(info.deliveryMode) || EMPTY_STRING}
        </ArcCellInfo>
      </ArcStack>

      <ArcCellInfo label='HUB' width='50%' labelProps={labelProps} textProps={textProps}>
        {info.hub || info.takeoff?.address || EMPTY_STRING}
      </ArcCellInfo>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Package Content' width='50%' labelProps={labelProps} sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }} textProps={textProps}>
          {info.packageContent || EMPTY_STRING}
        </ArcCellInfo>
        <ArcCellInfo label='Client Name' width='50%' labelProps={labelProps} sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }} textProps={textProps}>
          {info.clientName || EMPTY_STRING}
        </ArcCellInfo>
      </ArcStack>
    </ArcStack>
  );
};

export default MissionDetailsGrid;
